<template>
  <div class="role-list-container">
    <h2>角色管理</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <div class="filter-group">
          <el-input
            v-model="searchQuery"
            placeholder="搜索角色名称"
            style="width: 300px; margin-right: 10px;"
            clearable
          >
            <template #prefix>
              <el-icon><search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" @click="handleSearch">
            <el-icon><search /></el-icon> 搜索
          </el-button>
        </div>
        <el-button type="primary" @click="handleCreate">
          <el-icon><plus /></el-icon> 添加角色
        </el-button>
      </div>
      
      <el-table :data="roles" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="角色名称" />
        <el-table-column prop="display_name" label="显示名称" />
        <el-table-column prop="description" label="角色描述" />
        <el-table-column prop="created_at" label="创建时间" width="180" />
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">
              <el-icon><edit /></el-icon> 编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">
              <el-icon><delete /></el-icon> 删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 角色编辑抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      :title="isEditMode ? '编辑角色' : '添加角色'"
      size="50%"
      direction="rtl"
      :before-close="handleDrawerClose"
    >
      <el-form
        ref="roleFormRef"
        :model="roleForm"
        :rules="rules"
        label-position="top"
        label-width="120px"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="角色名称" prop="name">
              <el-input v-model="roleForm.name" placeholder="请输入角色名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="显示名称" prop="display_name">
              <el-input v-model="roleForm.display_name" placeholder="请输入角色显示名称" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="角色描述" prop="description">
              <el-input v-model="roleForm.description" type="textarea" placeholder="请输入角色描述" rows="4" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="菜单权限">
              <el-tree
                ref="menuTreeRef"
                :data="menuTree"
                :props="menuTreeProps"
                show-checkbox
                default-expand-all
                node-key="id"
                :default-checked-keys="roleForm.menu_ids"
                @check="handleCheck"
              />
            </el-form-item>
          </el-col>
        </el-row>
        
        <div class="form-actions">
          <el-button @click="handleDrawerClose">取消</el-button>
          <el-button type="primary" @click="handleFormSubmit">提交</el-button>
        </div>
      </el-form>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Edit, Delete } from '@element-plus/icons-vue';
import * as accountApi from '../../api/account';
import menuApi from '../../api/menu';

export default {
  name: 'RoleList',
  components: {
    Search,
    Plus,
    Edit,
    Delete
  },
  setup() {
    // 列表数据
    const roles = ref([]);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 抽屉相关状态
    const drawerVisible = ref(false);
    const isEditMode = ref(false);
    const roleFormRef = ref(null);
    const menuTreeRef = ref(null);
    const editingRoleId = ref(null);
    
    // 菜单数据
    const menuTree = ref([]);
    const menuTreeProps = {
      label: 'label',
      children: 'children'
    };
    
    // 角色表单数据
    const roleForm = reactive({
      name: '',
      display_name: '',
      description: '',
      menu_ids: []
    });
    
    // 表单验证规则
    const rules = {
      name: [
        { required: true, message: '请输入角色名称', trigger: 'blur' },
        { min: 2, max: 20, message: '角色名称长度在 2 到 20 个字符', trigger: 'blur' }
      ],
      display_name: [
        { required: true, message: '请输入角色显示名称', trigger: 'blur' },
        { min: 2, max: 20, message: '角色显示名称长度在 2 到 20 个字符', trigger: 'blur' }
      ]
    };
    
    // 获取菜单树数据
    const fetchMenuTree = async () => {
      try {
        const response = await menuApi.getMenus();
        menuTree.value = response.data || [];
      } catch (error) {
        ElMessage.error('获取菜单树数据失败');
        console.error(error);
        menuTree.value = [];
      }
    };
    
    // 获取角色列表
    const fetchRoles = async () => {
      loading.value = true;
      try {
        const response = await accountApi.fetchRolesWithPagination({
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchQuery.value
        });
        // 确保roles始终是数组，避免Element Plus表格出错
        roles.value = Array.isArray(response.data?.data) ? response.data.data : [];
        total.value = response.data?.total || 0;
      } catch (error) {
        ElMessage.error('获取角色列表失败');
        console.error(error);
        // 出错时确保roles是数组
        roles.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };
    
    // 页面加载时获取数据
    onMounted(() => {
      fetchRoles();
    });
    
    // 打开创建角色抽屉
    const handleCreate = async () => {
      isEditMode.value = false;
      // 获取菜单树数据
      await fetchMenuTree();
      // 重置表单
      Object.assign(roleForm, {
        name: '',
        display_name: '',
        description: '',
        menu_ids: []
      });
      drawerVisible.value = true;
    };
    
    // 打开编辑角色抽屉
    const handleEdit = async (role) => {
      isEditMode.value = true;
      editingRoleId.value = role.id;
      // 获取菜单树数据
      await fetchMenuTree();
      // 获取角色详情（包含菜单权限）
      try {
        const response = await accountApi.fetchRoleDetail(role.id);
        const roleDetail = response.data;
        // 填充表单数据
        Object.assign(roleForm, {
          name: roleDetail.name,
          display_name: roleDetail.display_name,
          description: roleDetail.description || '',
          menu_ids: roleDetail.menus?.map(menu => menu.id) || []
        });
        drawerVisible.value = true;
      } catch (error) {
        ElMessage.error('获取角色详情失败');
        console.error(error);
      }
    };
    
    // 关闭抽屉
    const handleDrawerClose = () => {
      drawerVisible.value = false;
    };
    
    // 处理菜单选择
    const handleCheck = () => {
      if (menuTreeRef.value) {
        // 获取所有选中的菜单ID，包括半选中的父节点
        roleForm.menu_ids = menuTreeRef.value.getCheckedKeys();
      }
    };
    
    // 表单提交
    const handleFormSubmit = async () => {
      if (!roleFormRef.value) return;
      
      await roleFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            if (isEditMode.value) {
              // 编辑模式
              await accountApi.updateRole(editingRoleId.value, roleForm);
              ElMessage.success('角色更新成功');
            } else {
              // 创建模式
              await accountApi.addRole(roleForm);
              ElMessage.success('角色创建成功');
            }
            
            // 关闭抽屉并刷新列表
            drawerVisible.value = false;
            fetchRoles();
          } catch (error) {
            // 处理具体的错误信息
            let errorMessage = isEditMode.value ? '角色更新失败' : '角色创建失败';
            
            // 检查是否有具体的错误信息
            if (error.response && error.response.data) {
              const responseData = error.response.data;
              if (responseData.message) {
                errorMessage = responseData.message;
              } else if (responseData.errors) {
                // 处理Laravel验证错误
                for (const field in responseData.errors) {
                  if (Object.prototype.hasOwnProperty.call(responseData.errors, field)) {
                    errorMessage = responseData.errors[field].join('; ');
                    break;
                  }
                }
              }
            }
            
            ElMessage.error(errorMessage);
            console.error(error);
          }
        }
      });
    };
    
    // 删除角色
    const handleDelete = (role) => {
      ElMessageBox.confirm('确定要删除该角色吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await accountApi.deleteRole(role.id);
          ElMessage.success('删除成功');
          fetchRoles();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchRoles();
    };
    
    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1;
      fetchRoles();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchRoles();
    };
    
    return {
      // 列表数据
      roles,
      searchQuery,
      currentPage,
      pageSize,
      total,
      loading,
      // 抽屉相关
      drawerVisible,
      isEditMode,
      roleForm,
      roleFormRef,
      menuTreeRef,
      rules,
      // 菜单数据
      menuTree,
      menuTreeProps,
      // 方法
      handleCreate,
      handleEdit,
      handleDrawerClose,
      handleCheck,
      handleFormSubmit,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      handleSearch
    };
  }
};
</script>

<style scoped>
.role-list-container {
  padding: 20px;
}

.role-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.form-actions .el-button {
  margin-left: 10px;
}
</style>
